/**
 * Biçimlendirme yardımcı işlevleri
 */

/**
 * Telefon numarasını formatlar
 * @param {string} phoneNumber - Formatlanacak telefon numarası
 * @param {string} format - Format şablonu (Varsayılan: Türk formatı)
 * @returns {string} Formatlanmış telefon numarası
 */
export const formatPhoneNumber = (phoneNumber, format = 'TR') => {
  if (!phoneNumber) return '';
  
  // Sadece sayıları al
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  if (format === 'TR') {
    // Türk formatı: XXX XXX XX XX
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
    } else if (digitsOnly.length <= 8) {
      return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
    } else {
      return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6, 8)} ${digitsOnly.slice(8, 10)}`;
    }
  } else if (format === 'US') {
    // ABD formatı: (XXX) XXX-XXXX
    if (digitsOnly.length <= 3) {
      return `(${digitsOnly}`;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  }
  
  return phoneNumber;
};

/**
 * Tarihi formatlar
 * @param {Date|string} date - Formatlanacak tarih
 * @param {string} format - Format türü ('short', 'medium', 'long')
 * @returns {string} Formatlanmış tarih
 */
export const formatDate = (date, format = 'short') => {
  const dateObj = date instanceof Date ? date : new Date(date);
  
  switch (format) {
    case 'short':
      return dateObj.toLocaleDateString('tr-TR');
    case 'medium':
      return dateObj.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    case 'long':
      return dateObj.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        weekday: 'long' 
      });
    default:
      return dateObj.toLocaleDateString('tr-TR');
  }
};

/**
 * Para birimini formatlar
 * @param {number} amount - Formatlanacak miktar
 * @param {string} currency - Para birimi ('TRY', 'USD', 'EUR', vb.)
 * @returns {string} Formatlanmış para birimi
 */
export const formatCurrency = (amount, currency = 'TRY') => {
  return new Intl.NumberFormat('tr-TR', { 
    style: 'currency', 
    currency: currency 
  }).format(amount);
};

/**
 * Kripto para birimini formatlar
 * @param {number|string} amount - Formatlanacak miktar
 * @param {string} symbol - Kripto sembolü ('BTC', 'ETH', 'IDCOIN', vb.)
 * @param {number} decimals - Ondalık basamak sayısı
 * @returns {string} Formatlanmış kripto para birimi
 */
export const formatCrypto = (amount, symbol, decimals = 8) => {
  const numericAmount = parseFloat(amount);
  const formattedAmount = numericAmount.toFixed(decimals);
  return `${formattedAmount} ${symbol}`;
};

/**
 * Cüzdan adresini kısaltır
 * @param {string} address - Kısaltılacak cüzdan adresi
 * @param {number} startLength - Başlangıçta gösterilecek karakter sayısı
 * @param {number} endLength - Sonda gösterilecek karakter sayısı
 * @returns {string} Kısaltılmış cüzdan adresi
 */
export const shortenWalletAddress = (address, startLength = 6, endLength = 4) => {
  if (!address) return '';
  if (address.length <= startLength + endLength) return address;
  
  return `${address.substring(0, startLength)}...${address.substring(address.length - endLength)}`;
};

/**
 * Metin içeriğini belirli bir uzunlukta kısaltır
 * @param {string} text - Kısaltılacak metin
 * @param {number} maxLength - Maksimum uzunluk
 * @returns {string} Kısaltılmış metin
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Demo kullanıcı verilerini formatlar
 * @param {Object} user - Kullanıcı objesi
 * @returns {Object} Formatlanmış kullanıcı verisi
 */
export const formatUserProfile = (user) => {
  if (!user) return getDefaultUserData();
  
  return {
    ...user,
    phone: user.phone ? formatPhoneNumber(user.phone) : '+90 532 123 45 67',
    emergencyContacts: user.emergencyContacts?.map(contact => ({
      ...contact,
      phone: formatPhoneNumber(contact.phone)
    })) || getDefaultEmergencyContacts(),
    birthDate: user.birthDate ? formatDate(user.birthDate) : '15 Mart 1990',
    joinDate: user.joinDate ? formatDate(user.joinDate) : formatDate(new Date())
  };
};

/**
 * Varsayılan kullanıcı verilerini döndürür
 * @returns {Object} Varsayılan kullanıcı verisi
 */
export const getDefaultUserData = () => ({
  name: 'Seçkin Aktunç',
  username: '@seckinaktunc',
  email: 'seckin@idapps.com',
  phone: '+90 532 123 45 67',
  birthDate: '15 Mart 1990',
  bloodType: 'AB+',
  height: '185 cm',
  weight: '80 kg',
  allergies: ['Polen', 'Fıstık', 'Antibiyotik'],
  conditions: ['Hipertansiyon', 'Astım'],
  medications: ['Lisinopril 10mg (günde 1)', 'Ventolin inhaler (gerektiğinde)'],
  languages: ['Türkçe', 'English', 'Deutsch'],
  emergencyContacts: getDefaultEmergencyContacts()
});

/**
 * Varsayılan acil durum kişilerini döndürür
 * @returns {Array} Acil durum kişileri listesi
 */
export const getDefaultEmergencyContacts = () => [
  { 
    name: 'Gazanfer Kalkan', 
    phone: '+90 532 123 45 67', 
    location: 'İstanbul, Türkiye', 
    relation: 'Baba' 
  },
  { 
    name: 'Dr. Ayşe Demir', 
    phone: '+90 555 987 65 43', 
    location: 'Anadolu Hastanesi', 
    relation: 'Doktor' 
  }
];

/**
 * Gelişmiş telefon numarası validasyonu
 * @param {string} phone - Doğrulanacak telefon numarası
 * @param {string} countryCode - Ülke kodu (varsayılan: TR)
 * @returns {boolean} Geçerli telefon numarası kontrolü
 */
export const isValidPhoneNumber = (phone, countryCode = 'TR') => {
  if (!phone || typeof phone !== 'string') return false;
  
  // Sadece sayıları al
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Minimum ve maksimum uzunluk kontrolleri
  if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
  
  // Ülke koduna göre validasyon
  switch (countryCode) {
    case 'TR':
      // Türk formatı: 10 hane (5xx xxx xx xx) veya 12 hane (+90 5xx xxx xx xx)
      if (digitsOnly.length === 10) {
        return /^5\d{9}$/.test(digitsOnly);
      } else if (digitsOnly.length === 12) {
        return /^905\d{9}$/.test(digitsOnly);
      }
      return false;
    case 'US':
      // ABD formatı: 10 hane
      return digitsOnly.length === 10 && /^\d{10}$/.test(digitsOnly);
    case 'GB':
      // İngiltere formatı: 10-11 hane
      return digitsOnly.length >= 10 && digitsOnly.length <= 11 && /^\d{10,11}$/.test(digitsOnly);
    default:
      // Genel format: 7-15 hane
      return /^\d{7,15}$/.test(digitsOnly);
  }
};

/**
 * E-posta validasyonu
 * @param {string} email - Doğrulanacak e-posta
 * @returns {boolean} Geçerli e-posta kontrolü
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.length <= 254;
};

/**
 * TC Kimlik No validasyonu
 * @param {string} tcNo - TC Kimlik numarası
 * @returns {boolean} Geçerli TC Kimlik No kontrolü
 */
export const isValidTCKimlikNo = (tcNo) => {
  if (!tcNo || typeof tcNo !== 'string') return false;
  
  const digitsOnly = tcNo.replace(/\D/g, '');
  
  // 11 hane kontrolü
  if (digitsOnly.length !== 11) return false;
  
  // İlk hane 0 olamaz
  if (digitsOnly[0] === '0') return false;
  
  // TC Kimlik algoritması
  const digits = digitsOnly.split('').map(Number);
  const checkSum1 = ((digits[0] + digits[2] + digits[4] + digits[6] + digits[8]) * 7 - 
                     (digits[1] + digits[3] + digits[5] + digits[7])) % 10;
  const checkSum2 = (digits[0] + digits[1] + digits[2] + digits[3] + digits[4] + 
                     digits[5] + digits[6] + digits[7] + digits[8] + digits[9]) % 10;
  
  return checkSum1 === digits[9] && checkSum2 === digits[10];
};

/**
 * IBAN validasyonu
 * @param {string} iban - IBAN numarası
 * @returns {boolean} Geçerli IBAN kontrolü
 */
export const isValidIBAN = (iban) => {
  if (!iban || typeof iban !== 'string') return false;
  
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  
  // IBAN uzunluk kontrolü (Türkiye için 26 karakter)
  if (!/^TR\d{24}$/.test(cleanIban)) return false;
  
  // IBAN algoritması
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);
  const numericString = rearranged.replace(/[A-Z]/g, char => 
    (char.charCodeAt(0) - 55).toString()
  );
  
  // Mod 97 kontrolü
  let remainder = 0;
  for (let i = 0; i < numericString.length; i++) {
    remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
  }
  
  return remainder === 1;
};

/**
 * Adres formatlaması
 * @param {object} address - Adres objesi
 * @returns {string} Formatlanmış adres
 */
export const formatAddress = (address) => {
  if (!address || typeof address !== 'object') return '';
  
  const parts = [
    address.street,
    address.district,
    address.city,
    address.postalCode,
    address.country
  ].filter(Boolean);
  
  return parts.join(', ');
};

/**
 * Kan grubu validasyonu
 * @param {string} bloodType - Kan grubu
 * @returns {boolean} Geçerli kan grubu kontrolü
 */
export const isValidBloodType = (bloodType) => {
  if (!bloodType || typeof bloodType !== 'string') return false;
  
  const validTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  return validTypes.includes(bloodType.toUpperCase());
};

/**
 * Yaş hesaplama
 * @param {string} birthDate - Doğum tarihi (YYYY-MM-DD)
 * @returns {number} Yaş
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return 0;
  
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return Math.max(0, age);
};